import { Navigate, Route, BrowserRouter as Router, Routes } from "react-router-dom";
import Home from "./pages/EntryPointPage";
import Login from "./pages/Login";
import UserListPage from "./pages/Users";
import Coupons from "./pages/BenefitListPage";
import VolatilityMessageDetail from "./pages/VolatilityMessageDetail";
import Payments from "./pages/Payments";
import "moment/locale/ko";
import "omoplay/dist/style.scss"
import MessagePage from "./pages/Messages";
import AutomateMessages from "./pages/AutomateMessages";
import AutomateMessageEditPage from "./pages/AutomateMessageEditPage";
import OMOConnectSettingPage from "./components/page/OMOConnectSettingPage";
import ProjectsPage from "./pages/Projects";
import ProjectSettings from "./pages/ProjectSettings";
import EventEdit from "./pages/EventEdit";
import EventPage from "./pages/EventPage";
import BenefitEdit from "./pages/BenefitEdit";
import ProjectDetailPage from "./pages/ProjectDetail";
import VolatilityMessageSettingsPage from "./pages/VolatilityMessageSettingsPage";
import PointMenuPage from "./pages/PointMenuPage";
import UserDetailPage from "./pages/UserDetail";
import OnlineOrderUsersPage from "./pages/OnlineOrderUsersPage";
import ProductListPage from "./pages/ProductListPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import AIVolatilityMessageListPage from "./pages/AIVolatilityMessageListPage";
import VolatilityMessageListPage from "./pages/VolatilityMessageListPage";
import AIVolatilityMessageEditPage from "./pages/AIVolatilityMessageEditPage";
import Join from "./pages/JoinPage";
import BrandPage from "./pages/BrandPage";
import ProductEditPage from "./pages/ProductEditPage";
import Services from "./pages/Services";
import OMOAgentSettingPage from "./components/page/OMOAgentSettingPage";
import {useRecoilState, useRecoilValue} from "recoil";
import {accountSelector, channelSelector, sidebarState} from "./store/selector/selector";
import CreditPage from "./pages/CreditPage";
import CreditDetailPage from "./pages/CreditDetailPage";
import ChannelRegistrationPage from "./components/page/ChannelRegistrationPage";
import FindIdPage from "./pages/FindIdPage";
import FindPwdPage from "./pages/FindPwdPage";
import NotFound from "./pages/NotFound";
import AllSpotPage from "./pages/AllSpots";
import OMOAgentTutorialPage from "./components/page/OMOAgentTutorialPage";
import AIVerification from "./components/page/AIVerfication";
import AuthorizationScreenPage from "./pages/AuthorizationScreenPage";
import AuthScreenSettingPage from "./components/page/AuthScreenSettingPage";
import {logout} from "./api/account/account.api";
import CustomersPage from "./pages/CustomersPage";
import AIBusiness from "./components/page/AIBusiness";
// AI 어시스턴트(Text-to-SQL) 라우트 제거에 따라 import 도 함께 뺐다. 아래 라우트 주석 참고.
// import AIAssistantPage from "./pages/AIAssistantPage";
import {useEffect} from "react";
import SystemAdminPage from "./pages/SystemAdminPage";
import MessageCalendarPage from "./pages/MessageCalendarPage";
import ErrorPage from "./pages/ErrorPage";
import OnboardingMessageListPage from "./pages/OnboardingMessageListPage";
import StatisticsMessagePage from "./pages/StatisticsMessagePage";
import GeneralSettingsPage from "./pages/GeneralSettingsPage";
import ChannelChoicePage from "./components/page/ChannelChoicePage";
import {isClarityStarted, startClarity} from "./libs/clarity";
import TossPaymentBilling from "./pages/TossPaymentBilling";
import PointUsersPage from "./pages/PointUsers";
import PointConfigurationPage from "./pages/PointConfigurationPage";
import PointViewPage from "./components/page/PointViewPage";
import WhiteSpacePage from "./pages/WhiteSpacePage";
import CouponPage from "./components/page/CouponPage";
import PaymentSuccessPage from "./components/page/PaymentSuccessPage";
import CouponUsagePage from "./components/page/CouponUsagePage";
import AiMessageGenerator from "./components/page/AiMessageGenerator";
import AIAutoSetupLayout from "./pages/AIAutoSetupLayout";
import OneTimeMessagePages from "./pages/OneTimeMessagePages";
import CustomerPointPage from "./components/page/CustomerPointPage";
import {PointServiceGuard} from "./components/ServiceGuard";
import DashboardPage from "./pages/DashboardPage";
import PaymentFailPage from "./components/page/PaymentFailPage";
import AIStickerGeneratorPage from "./pages/AIStickerGeneratorPage";
import ChannelConnectWizard from "./components/page/ChannelConnectWizard";
import AdminChannelWizard from "./components/page/AdminChannelWizard";
import InitSettingWizard from "./components/page/InitSettingWizard";
import CardRegisterByLink from "./pages/CardRegisterByLink";
import InvoiceByLink from "./pages/InvoiceByLink";
import TossPaymentBillingLink from "./pages/TossPaymentBillingLink";
import KakaoLoginTest from "./components/page/KakaoLoginTest";
import InitializeSetupPage from "./pages/InitializeSetupPage";
import DuplicateAccount from "./pages/DuplicateAccount";

function App() {
  const account = useRecoilValue(accountSelector);
  const channel = useRecoilValue(channelSelector)
  const [, setOpenSidebar] = useRecoilState(sidebarState)

  const permissions = account?.channels?.find(ch => ch.id === channel)?.permissions
  const showConnect = permissions?.some(pm => pm.serviceType === "Connect")
  const showScreen = permissions?.some(pm => pm.serviceType === "Screen")
  /*
    내부 관리자(system) 계정 여부. 서버가 `GET /account` 에 담아 내려준다.
    판정 기준은 `account_permission.service_type='System'` 한 곳이다. 계정 id 하드코딩 금지.
  */
  const isSystemAdmin = account?.isSystemAdmin === true


  useEffect(() => {
    if (localStorage.getItem("channelState") && isNaN(channel)) {
      logout()
    }
  }, []);

  /*
    Microsoft Clarity(세션 녹화). 🔴 /sysadmin 이 그려질 수 있는 페이지에는 싣지 않는다 — 규칙과 이유는 libs/clarity.js.
    관리자 판정이 뒤늦게 true 가 되는 경우(정상 흐름에선 없다)에는 이미 실린 Clarity 를 걷어내려고 새로고침한다.
    새로고침 뒤에는 관리자이므로 다시 싣지 않는다.
  */
  useEffect(() => {
    if (isSystemAdmin && isClarityStarted()) {
      window.location.reload()
      return
    }
    startClarity({ isSystemAdmin, pathname: window.location.pathname })
  }, [isSystemAdmin]);

  /**
   * 좁은 화면에서는 좌측 메뉴를 접은 상태로 시작한다.
   *
   * ⚠️ 이름이 오해를 부른다 — `sidebarState`(=`openSidebar`)는 **"접혔는가"** 를 뜻한다.
   * `Layout.js` 가 `<Sidebar collapsed={openSidebar} ...>` 로 넘긴다. 그래서 좁을 때 `true` 가 맞다.
   * (같은 값이 'PC 브라우저 권장' 안내 배너 노출에도 쓰인다 — `Layout.js:49`)
   *
   * ## 2026-08-21 보완 — 리사이즈·회전 대응
   *
   * 종전에는 **마운트 시 1회만** 판정했다(`setOpenSidebar(window.innerWidth < 1024)`).
   * SPA 라 화면을 옮겨도 `App` 이 다시 마운트되지 않으므로, 세로↔가로 회전이나 창 크기 변경에는
   * 반응하지 않았다.
   *
   * **브레이크포인트를 넘는 순간에만** 값을 바꾼다 — 매 resize 마다 덮어쓰면
   * 좁은 화면에서 사용자가 직접 펼친 메뉴를 계속 접어버린다.
   */
  useEffect(() => {
    const NARROW_WIDTH = 1024
    const isNarrow = () => window.innerWidth < NARROW_WIDTH

    let wasNarrow = isNarrow()
    setOpenSidebar(wasNarrow)

    const handleResize = () => {
      const narrowNow = isNarrow()
      if (narrowNow === wasNarrow) return

      wasNarrow = narrowNow
      setOpenSidebar(narrowNow)
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
    // recoil setter 는 참조가 고정이라 의존성에 넣어도 재실행되지 않는다.
  }, [setOpenSidebar]);

  return (
    <Router>
      <Routes>
        <Route path="/spots" element={<Home />} />
        <Route path="/event" element={<EventPage />} />
        <Route path="/event/edit" element={<EventEdit />} />
        <Route path="/benefit/edit" element={<BenefitEdit />} />
        <Route path="/volatility-message" element={<VolatilityMessageListPage />} />
        <Route
            path="/volatility-message/edit"
            element={<VolatilityMessageSettingsPage />}
        />
        <Route path="/message/calendar" element={<MessageCalendarPage />} />
        <Route path="/benefit" element={<Coupons />} />
        <Route path="/users" element={<UserListPage />} />
        <Route path="/user/detail" element={<UserDetailPage />} />
        <Route path="/messages" element={<MessagePage />} />
        <Route path="/users-statistics" element={<AllSpotPage />} />
        <Route path="/automate-message" element={<AutomateMessages />} />
        <Route path="/automate-message/edit" element={<AutomateMessageEditPage />} />
        <Route path="/project" element={<ProjectsPage />} />
        <Route path="/project/edit" element={<ProjectSettings />} />
        <Route path="/project/detail" element={<ProjectDetailPage />} />
        <Route path="/point-menu" element={<PointMenuPage />} />
        <Route path="/online/orders" element={<OnlineOrderUsersPage />} />
        <Route path="/volatility-message/detail" element={<VolatilityMessageDetail />} />
        {/*
          AI 어시스턴트(Text-to-SQL) 라우트 제거. `[SECURITY_RISK]`
          서버에서 `/ai/assistant/**` 를 denyAll 로 차단했다(redirect-service `SecurityConfig`).
          라우트를 남겨두면 화면은 열리고 API 만 403 이 되어 '빈 화면 + 콘솔 에러' 가 된다 →
          라우트를 빼서 NotFound 로 보낸다. 좌측 메뉴는 이미 주석 처리돼 있었다(`Layout.js`).
          컴포넌트(`pages/AIAssistantPage.js`, `components/page/AIAssistant.js`)는 되살릴 때를 위해 남겨둔다.
          되살리는 조건 = `_ai-runs/2026-08-26_003_genai_prompt_inventory/004_sql_exec_defense.md`
        */}
        <Route path="/product" element={<ProductListPage />} />
        <Route path="/product/detail" element={<ProductDetailPage />} />
        <Route path="/product/edit" element={<ProductEditPage />} />
        <Route path="/onboarding-message" element={<OnboardingMessageListPage />} />
        <Route path="/ai/volatility-message" element={<AIVolatilityMessageListPage />} />
        <Route path="/ai/volatility-message/edit" element={<AIVolatilityMessageEditPage />} />
        <Route path="/statistics/message" element={<StatisticsMessagePage />} />
        {
            showConnect && (
                <>
                  <Route path="/credit" element={<CreditPage />} />
                  <Route path="/credit/detail" element={<CreditDetailPage />} />
                </>
            )
        }
        {
          showScreen && (
                <Route path="/auth/screen" element={<AuthorizationScreenPage />} />
            )
        }
        <Route path="/" element={<Services />} />
        <Route path="/customers" element={<CustomersPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/services" element={<Services />} />
        <Route path="/brand" element={<BrandPage />} />
        <Route path="/account/join" element={<Join />} />
        <Route path="/account/find/id" element={<FindIdPage />} />
        <Route path="/account/find/pwd" element={<FindPwdPage />} />
        <Route path="/account/duplicate" element={<DuplicateAccount />} />
        <Route path="/settings/choice" element={<ChannelChoicePage />} />
        <Route path="/settings/connect" element={<OMOConnectSettingPage />} />
        <Route path="/settings/agent" element={<OMOAgentSettingPage />} />
        <Route path="/settings/screen" element={<AuthScreenSettingPage />} />
        <Route path="/settings" element={<GeneralSettingsPage />} />
        <Route path="/channel/registration" element={<ChannelRegistrationPage />} />
        <Route path="/agent/tutorial" element={<OMOAgentTutorialPage />} />
        <Route path="/verficiation/test" element={<AIVerification />} />
        <Route path="/business/test" element={<AIBusiness />} />
        {/* 시스템 관리자(A2). 탭 상태 대신 **경로**로 화면을 나눈다 —
            새로고침·뒤로가기·링크 공유가 되고, 어느 화면을 보고 있었는지 URL 에 남는다.
            /sysadmin 진입 시 대시보드로 보낸다(replace: 뒤로가기가 리다이렉트에 갇히지 않게). */}
        {/*
          🔴 `/sysadmin` 하위 라우트는 전부 **조건부 등록**이다(2026-09-22 사용자 지시).

          종전에는 누구에게나 등록돼 있어서, 일반 계정이 주소를 직접 치면
          **탭 께데기는 렌더되고** API 만 403 이 나 alert 가 떴다.
          데이터가 새지는 않았지만 관리자 화면의 존재와 메뉴 구성이 그대로 드러났다.
          이제 관리자가 아니면 `*` → NotFound 로 간다 — 아래 `/ai/setup`·`/init/setup` 과 같다.

          ⚠️ 판정은 가림일 뿐이고 실제 차단은 서버가 한다
             (`SecurityConfig` 의 `/sysadmin/**`·`/system/**`·`/wizard/**` → `sysadminOnly`).
             설치 진행자·백엔드 연동 작업자도 그 권한이 있어야 화면이 동작했으므로
             라우트를 막아도 실질 사용자가 바뀜지 않는다.
        */}
        {isSystemAdmin && <Route path="/sysadmin" element={<Navigate to="/sysadmin/dashboard" replace />} />}
        {isSystemAdmin && <Route path="/sysadmin/client/:channelId" element={<SystemAdminPage />} />}
        {/*
          관리자 위저드(2026-09-07 이전 `/wizard/admin`).

          고객 온보딩 위저드의 주소가 `/wizard` 라서 두 화면이 같은 경로 아래에 있었고,
          "위저드" 라는 말이 무엇을 가리키는지 매번 헷갈렸다. 백엔드 API 도 똑같이
          `/wizard` 하나를 공유해서, 관리자 API 만 막으려 해도 경로로는 구분이 안 됐다.
          ⇒ 관리자용은 화면·API 모두 `/sysadmin` 아래로 옮겼다.

          ⚠️ `/sysadmin/:section` 보다 **위**에 두었다. react-router v6 은 정적 세그먼트를
          우선하므로 순서가 결정적이지는 않지만, 읽는 사람이 헷갈리지 않게 구체적인 것을 먼저 쓴다.

          ⛔ 구 경로(`/wizard/admin`·`/wizard/login/test`) 리다이렉트는 **제거했다**(2026-09-08).
             온보딩을 새로 만들어 가는 중이라 지킬 북마크가 없고, 남겨 두면 같은 화면에 주소가
             둘인 상태가 굳는다. 구 주소로 들어오면 `*` → NotFound 다.
        */}
        {isSystemAdmin && <Route path="/sysadmin/wizard/backend" element={<AdminChannelWizard />} />}
        {/*
          초기 세팅 위저드(2026-09-07 신규) — '설치 진행자' 용.

          위 `/backend` 와 담당자가 다르다. 설치 진행자가 고객과 통화하며 확인한 값
          (혜택 1~5등·재방문·생일·스티커 주소)을 남기고 백엔드 연동 작업자에게 **비동기로** 넘긴다.
          설계: `_ai-runs/2026-09-07_002_install_wizard/001_analysis_and_design.md`

          ⚠️ 2026-09-08 에 `install` → `init-setting` 으로 주소를 바꿨다(온보딩 용어 통일).
             구 주소 리다이렉트는 두지 않는다 — 온보딩을 새로 만들어 가는 중이라 지킬 북마크가 없다.
        */}
        {isSystemAdmin && <Route path="/sysadmin/wizard/init-setting" element={<InitSettingWizard />} />}
        {isSystemAdmin && <Route path="/sysadmin/wizard/login-test" element={<KakaoLoginTest />} />}
        {isSystemAdmin && <Route path="/sysadmin/:section" element={<SystemAdminPage />} />}
        {/*
          적립(포인트) 화면 4개 — 신청하지 않은 고객에게는 안내로 바뀜다(2026-09-22).

          🔑 라우트를 **없애지 않는다.** 메뉴는 판매를 위해 일부러 남겨 두었으므로
             들어오면 NotFound 가 아니라 "신청이 필요합니다" 를 보여줘야 한다.
             (관리자 전용인 `/sysadmin`·`/ai/setup` 과 정반대로 다룬다 — 그쪽은 팔 것이 아니므로 숨긴다.)

          ⚠️ 아래 둘은 `Layout` 을 쓰지 않는 **매장 전체화면**이라 여기서 감싼다.
             `/point/users`·`/point/config` 는 사이드바를 남겨야 해서
             각 page 파일 안(`Layout` 내부)에서 감쌉다.
        */}
        <Route path="/point/users" element={<PointUsersPage />} />
        <Route path="/point/config" element={<PointConfigurationPage />} />
        <Route path="/point/view" element={<PointServiceGuard><PointViewPage /></PointServiceGuard>} />
        <Route path="/point/customer" element={<PointServiceGuard><CustomerPointPage /></PointServiceGuard>} />
        <Route path="/payments" element={<Payments />} />
        <Route path="/error" element={<ErrorPage />} />
        <Route path="/payments/toss/billing" element={<TossPaymentBilling />} />
        {/*
          가입 위저드 (5) 카드 등록 복귀 경로(2026-10-12). 같은 처리이고 **돌아갈 곳만** 다르다 —
          `/wizard?step=payment` 로 가야 (6) 신청내용 확인·결제 화면이 이어진다.
        */}
        <Route path="/payments/toss/billing/wizard" element={<TossPaymentBilling returnPath="/wizard?step=payment" />} />
        {/*
          사업자번호 확인 고유링크(2026-09-08). **로그인 없이** 열리는 고객용 두 화면이다.
          `/card/register` 에서 사업자등록번호를 확인하고, 토스를 거쳐
          `/payments/toss/billing/link` 로 돌아온다. URL 에 계정 식별자가 없다.
          설계 = `_ai-runs/2026-09-07_002_install_wizard/011_round5_card_link.md`
        */}
        <Route path="/card/register" element={<CardRegisterByLink />} />
        <Route path="/payments/toss/billing/link" element={<TossPaymentBillingLink />} />
        {/*
          결제 예정 안내 문자의 모바일 고지서(2026-10-02). **로그인 없이** 열린다(PUBLIC_PATHS 에도 있어야 한다).
          사업자등록번호를 맞히면 그 달 확정 고지서만 보여준다. 설계 = `_ai-runs/2026-10-02_001_billing_payment_notice/`
        */}
        <Route path="/invoice/view" element={<InvoiceByLink />} />
        <Route path="/advanced/coupon" element={<CouponPage />} />
        <Route path="/advanced/coupon/usage" element={<CouponUsagePage />} />
        <Route path="/payment/success" element={<PaymentSuccessPage />} />
        <Route path="/payment/fail" element={<PaymentFailPage />} />
        <Route path="/ai/message/generator" element={<AiMessageGenerator />} />
        {/*
          'AI 이벤트 생성'(2026-09-09 부터 내부 관리자 전용).

          고객이 임의로 실행해 **불필요한 AI 비용 + CS** 가 발생했다 ⇒ 기능의 용도를
          '관리자 초기 세팅' 으로 좁혔다. 서버도 `/ai/generate/project`·`/ai/generate/event` 를
          sysadmin 전용으로 막았다(redirect-service `SecurityConfig`).

          라우트를 조건부로 **등록하지 않는** 이유 = 위 AI 어시스턴트 차단과 같다.
          남겨두면 주소를 직접 쳤을 때 화면은 열리고 API 만 403 이 되어 '빈 화면 + 콘솔 에러' 가 된다.
          관리자가 아니면 `*` → NotFound 로 간다.
        */}
        {isSystemAdmin && <Route path="/ai/setup" element={<AIAutoSetupLayout />} />}
        <Route path="/none" element={<WhiteSpacePage />} />
        <Route path="/message/onetime/edit" element={<OneTimeMessagePages />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/sticker" element={<AIStickerGeneratorPage />} />
        {/* 고객 온보딩 위저드. 관리자용은 /sysadmin/wizard/* 로 분리했다(위쪽 참고). */}
        <Route path="/wizard" element={<ChannelConnectWizard />} />
        {/*
          초기 세팅 화면 = 'AI 빠른 자동화 일괄 세팅' + 'AI 이벤트 생성' + 스티커 생성이
          한 화면에 붙은 구성(`InitializeSetup.js`). 2026-09-09 부터 내부 관리자 전용.

          🔴 종전에는 `Layout.js` 가 프로젝트 0건인 Connect 고객을 여기로 **강제 이동**시켰다.
             그 이동도 관리자로 한정했다(`Layout.js` 의 같은 날짜 주석 참고).
             ⇒ 신규 고객이 초기 세팅 화면으로 자동 유입되지 않는다. 의도한 변경이다.
        */}
        {isSystemAdmin && <Route path="/init/setup" element={<InitializeSetupPage />} />}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
  );
}

export default App;
